import { computed, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { Router } from '~/core';

export interface RouteData {
    name: string;
    displayName: string;
    active: boolean;
    params?: Record<any, any>;
    eggTag?: string | string[];
    nestTag?: string | string[];
    permission?: string;
    feature?: string;
    addon?: string;
    icon?: string;
    routes?: RouteData[];
    disabled?: boolean;
    allowWhenInstalling?: boolean;
}

// Global reactive state for navigation routes (set by router afterEach)
const navigationRoutes = ref<RouteData[]>([]);
const previousRoute = ref<RouteLocationRaw | null>(null);

export const useNavigation = () => {
    // Current route info derived from Vue Router
    const currentRoute = computed(() => ({
        name: Router.currentRoute.value.name?.toString() || 'unknown',
        displayName: Router.currentRoute.value.name?.toString() || 'unknown',
        active: true,
        icon: (Router.currentRoute.value.meta?.icon as string | undefined) || 'unknown',
        params: Router.currentRoute.value.params,
        permission: Router.currentRoute.value.meta?.permission as string | undefined,
        feature: Router.currentRoute.value.meta?.feature as string | undefined,
        addon: Router.currentRoute.value.meta?.addon as string | undefined,
        eggTag: Router.currentRoute.value.meta?.eggTag as string | string[] | undefined,
        nestTag: Router.currentRoute.value.meta?.nestTag as string | string[] | undefined,
        stretch: Router.currentRoute.value.meta?.stretch,
        fullscreen: Router.currentRoute.value.meta?.fullscreen,
        noBreadcrumbs: Router.currentRoute.value.meta?.noBreadcrumbs,
        noPadding: Router.currentRoute.value.meta?.noPadding,
        disableNumberFlow: Router.currentRoute.value.meta?.disableNumberFlow,
    }));

    return {
        // Navigation routes (set by router afterEach)
        routes: computed(() => navigationRoutes.value),
        previousRoute: computed(() => previousRoute.value),

        // Current route info
        currentRoute,
        currentRouteName: computed(() => currentRoute.value.name),
        currentRouteDisplayName: computed(() => currentRoute.value.displayName),
        currentRouteIcon: computed(() => currentRoute.value.icon),

        // Actions for router to call
        setRoutes: (routes: RouteData[]) => {
            navigationRoutes.value = routes;
        },
        setPreviousRoute: (route: RouteLocationRaw | null) => {
            previousRoute.value = route;
        },
        navigateToPreviousRoute: async (fallbackRoute: RouteLocationRaw = { name: 'index' }) => {
            const route = previousRoute.value;

            if (route) {
                const resolvedRoute = Router.resolve(route);
                if (resolvedRoute.fullPath !== Router.currentRoute.value.fullPath) {
                    await Router.replace(route);
                    return;
                }
            }

            await Router.replace(fallbackRoute);
        },

        // Helper getters
        getRouteByName: (routeName: string): RouteData | undefined => {
            const findRoute = (routeList: RouteData[]): RouteData | undefined => {
                for (const route of routeList) {
                    if (route.name === routeName) {
                        return route;
                    }
                    if (route.routes) {
                        const found = findRoute(route.routes);
                        if (found) return found;
                    }
                }
                return undefined;
            };
            return findRoute(navigationRoutes.value);
        },

        hasDropdownRoutes: (routeName: string): boolean => {
            const route = navigationRoutes.value.find((r) => r.name === routeName);
            return !!(route?.routes && route.routes.length > 0);
        },

        getDropdownRoutes: (routeName: string): RouteData[] => {
            const route = navigationRoutes.value.find((r) => r.name === routeName);
            return route?.routes || [];
        },
    };
};
